/*
  Loom Collection product page features (rebuilt for Symmetry 8, Oct 2026)
  - <abz-estimated-delivery>: works out delivery date ranges (same rules as the old theme)
  - <loom-upsell>: bulb / complementary product picker
  - Adds the chosen upsell items to the cart together with the main product, using the theme's own add-to-cart flow
*/
(() => {
  /* ---------- Estimated delivery ---------- */
  const CUTOFF_HOUR = 9; // orders after 09:00 count from the next day
  const BUFFER_DAYS = { dubai: 1, emirates: 3, gcc: 7 };

  const addDaysSkippingSunday = (date, daysToAdd, includeToday) => {
    const result = new Date(date);
    let days = daysToAdd;
    if (includeToday && result.getDay() !== 0) days -= 1;
    while (days > 0) {
      result.setDate(result.getDate() + 1);
      if (result.getDay() !== 0) days -= 1;
    }
    return result;
  };

  const isArabic = (document.documentElement.lang || '').toLowerCase().startsWith('ar');
  const formatDate = (date) => `${date.getDate()} ${date.toLocaleString(isArabic ? 'ar-AE' : 'en-GB', { month: isArabic ? 'long' : 'short' })}`;

  if (!customElements.get('abz-estimated-delivery')) {
    customElements.define('abz-estimated-delivery', class extends HTMLElement {
      connectedCallback() {
        const now = new Date();
        const baseDate = new Date();
        if (now.getHours() >= CUTOFF_HOUR) baseDate.setDate(baseDate.getDate() + 1);

        this.querySelectorAll('[data-delivery-time]').forEach((el) => {
          const deliveryTime = parseInt(el.dataset.deliveryTime, 10);
          if (Number.isNaN(deliveryTime)) {
            el.textContent = '';
            return;
          }
          const region = el.dataset.region || 'dubai';
          const buffer = BUFFER_DAYS[region] || 1;
          let start;
          let end;
          if (region === 'dubai') {
            start = addDaysSkippingSunday(baseDate, deliveryTime, true);
            end = addDaysSkippingSunday(start, buffer, false);
          } else {
            start = addDaysSkippingSunday(baseDate, buffer, true);
            end = addDaysSkippingSunday(baseDate, deliveryTime, true);
          }
          el.textContent = `${formatDate(start)} - ${formatDate(end)}`;
        });
      }
    });
  }

  /* ---------- Upsell picker ---------- */
  if (!customElements.get('loom-upsell')) {
    customElements.define('loom-upsell', class extends HTMLElement {
      connectedCallback() {
        if (this.dataset.ready) return;
        this.dataset.ready = 'true';
        this.select = this.querySelector('.upsell-select');
        this.toggleBtn = this.querySelector('.current-option');
        this.options = [...this.querySelectorAll('.option_value')];
        if (!this.options.length) return;

        this.choose(this.options[0], false);

        this.toggleBtn.addEventListener('click', (evt) => {
          evt.preventDefault();
          this.setOpen(!this.select.classList.contains('open'));
        });
        this.options.forEach((opt) => {
          opt.addEventListener('click', () => this.choose(opt, true));
          opt.addEventListener('keydown', (evt) => {
            if (evt.key === 'Enter' || evt.key === ' ') {
              evt.preventDefault();
              this.choose(opt, true);
            }
          });
        });
        document.addEventListener('click', (evt) => {
          if (!this.contains(evt.target)) this.setOpen(false);
        });
        this.addEventListener('keydown', (evt) => {
          if (evt.key === 'Escape') {
            this.setOpen(false);
            this.toggleBtn.focus();
          }
        });
      }

      setOpen(open) {
        this.select.classList.toggle('open', open);
        this.toggleBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
      }

      choose(opt, close) {
        const n = this.dataset.upsell;
        this.options.forEach((o) => {
          o.classList.remove(`Bulbselected${n}`, 'Bulbselected');
          o.setAttribute('aria-selected', 'false');
        });
        opt.classList.add(`Bulbselected${n}`, 'Bulbselected');
        opt.setAttribute('aria-selected', 'true');
        this.selected = opt;

        const img = this.querySelector('.current-option-img');
        const wrap = this.querySelector('.current-option-img-wrapper');
        const optImg = opt.querySelector('.upsell-img img');
        if (img && optImg) img.src = optImg.src;
        const isNo = opt.dataset.value === 'no';
        img?.classList.toggle('small-img', isNo);
        wrap?.classList.toggle('small-img-wrapper', isNo);
        this.querySelector('.loom-upsell-current-text').textContent = opt.textContent.trim().replace(/\s+/g, ' ');
        if (close) {
          this.setOpen(false);
          this.toggleBtn.focus();
        }
      }

      /** @returns {{id: number, qty: number, slot: string} | null} */
      getSelection() {
        const opt = this.selected;
        if (!opt || opt.dataset.value !== 'yes' || !opt.dataset.itemId) return null;
        const qty = parseInt(opt.dataset.bulbQty, 10);
        return { id: parseInt(opt.dataset.itemId, 10), qty: Number.isNaN(qty) || qty < 1 ? 1 : qty, slot: `upsell${this.dataset.upsell}` };
      }
    });
  }

  /* ---------- Add upsells with the main product ----------
     The theme builds its add-to-cart request with new FormData(form). The "formdata" event lets us
     turn that single item into a bundle (main product + chosen upsells) without replacing the theme's code,
     so the cart drawer, errors and loading states keep working as normal. */
  document.addEventListener('formdata', (evt) => {
    const form = evt.target;
    if (!form.matches || !form.matches('.js-product-form')) return;
    // The sticky Add to cart bar has its own small form, so read the choices from the main product form
    const scope = form.closest('sticky-atc')
      ? (form.closest('.section-main-product') || document).querySelector('product-form:not(sticky-atc)') || form
      : form;
    const picks = [...scope.querySelectorAll('loom-upsell')]
      .map((el) => (el.getSelection ? el.getSelection() : null))
      .filter(Boolean);
    if (!picks.length) return;

    const fd = evt.formData;
    const mainId = fd.get('id');
    if (!mainId) return;
    const mainQty = Math.max(parseInt(fd.get('quantity'), 10) || 1, 1);
    const bundle = Math.random().toString(16).slice(2, 8);

    const props = [];
    [...fd.entries()].forEach(([key, value]) => {
      const match = key.match(/^properties\[(.+)\]$/);
      if (match) props.push([match[1], value]);
    });
    ['id', 'quantity'].forEach((k) => fd.delete(k));
    props.forEach(([k]) => fd.delete(`properties[${k}]`));

    fd.append('items[0][id]', mainId);
    fd.append('items[0][quantity]', mainQty);
    props.forEach(([k, v]) => fd.append(`items[0][properties][${k}]`, v));
    fd.append('items[0][properties][_randomNumber]', bundle);
    fd.append('items[0][properties][_main]', 'true');

    picks.forEach((p, i) => {
      const idx = i + 1;
      fd.append(`items[${idx}][id]`, p.id);
      fd.append(`items[${idx}][quantity]`, p.qty * mainQty);
      fd.append(`items[${idx}][properties][_randomNumber]`, bundle);
      fd.append(`items[${idx}][properties][_sub]`, p.slot);
    });
  });

  /* ---------- Sister-product colour swatches: show the hovered colour name ---------- */
  const swatchLabel = (sw) => sw.closest('.loom-swatches')?.querySelector('.label__value');
  document.addEventListener('mouseover', (evt) => {
    const sw = evt.target.closest?.('.loom-swatch');
    if (!sw) return;
    const label = swatchLabel(sw);
    if (label) label.textContent = sw.dataset.colorName;
  });
  document.addEventListener('mouseout', (evt) => {
    const sw = evt.target.closest?.('.loom-swatch');
    if (!sw || sw.contains(evt.relatedTarget)) return;
    const label = swatchLabel(sw);
    if (label) label.textContent = label.dataset.default;
  });

  /* ---------- "Designed around you" pop-up (native dialog) ---------- */
  document.addEventListener('click', (evt) => {
    const opener = evt.target.closest?.('[data-loom-dialog-open]');
    if (opener) {
      const dialog = document.getElementById(opener.dataset.loomDialogOpen);
      if (dialog && typeof dialog.showModal === 'function') {
        dialog.showModal();
        document.documentElement.classList.add('loom-dialog-open');
      }
      return;
    }
    const closer = evt.target.closest?.('[data-loom-dialog-close]');
    if (closer) {
      closer.closest('dialog')?.close();
      return;
    }
    // Click on the dimmed background closes the pop-up
    if (evt.target.matches?.('dialog.loom-design-popup__dialog')) evt.target.close();
  });
  document.addEventListener('close', (evt) => {
    if (evt.target.matches?.('dialog.loom-design-popup__dialog')) {
      document.documentElement.classList.remove('loom-dialog-open');
    }
  }, true);
})();

/* ---------- Configuration option images: zoom in and centre the product ----------
   Finds the product inside each (white-background) option photo and scales/positions
   it so it sits centred in the box with a small margin. Room photos are left as they are. */
(() => {
  const SEL = '.opt-label--image .opt-label__media img';
  const FILL = 0.86;          // product takes up to 86% of the box width/height
  const cache = new Map();    // src -> bbox (fractions) or null

  const findBox = (img) => new Promise((resolve) => {
    const src = img.currentSrc || img.src;
    if (cache.has(src)) return resolve(cache.get(src));
    const probe = new Image();
    probe.onload = () => {
      try {
        const N = 120, c = document.createElement('canvas');
        c.width = N; c.height = N;
        const ctx = c.getContext('2d', { willReadFrequently: true });
        ctx.drawImage(probe, 0, 0, N, N);
        const px = ctx.getImageData(0, 0, N, N).data;
        let x0 = N, y0 = N, x1 = -1, y1 = -1;
        for (let y = 0; y < N; y++) for (let x = 0; x < N; x++) {
          const i = (y * N + x) * 4;
          if (px[i + 3] > 20 && Math.min(px[i], px[i + 1], px[i + 2]) < 235) {
            if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y;
          }
        }
        let box = null;
        if (x1 >= 0) {
          box = { x0: x0 / N, x1: (x1 + 1) / N, y0: y0 / N, y1: (y1 + 1) / N };
          // full-frame photo (e.g. room setting): leave it alone
          if ((box.x1 - box.x0) > 0.97 && (box.y1 - box.y0) > 0.97) box = null;
        }
        cache.set(src, box); resolve(box);
      } catch (e) { cache.set(src, null); resolve(null); }
    };
    probe.onerror = () => { cache.set(src, null); resolve(null); };
    probe.src = src;
  });

  const place = async (img) => {
    const wrap = img.parentElement;
    if (!wrap) return;
    const box = await findBox(img);
    if (!box) { img.classList.remove('loom-cfg-fit'); img.removeAttribute('style'); return; }
    const W = wrap.clientWidth, H = wrap.clientHeight;
    if (!W || !H) return;
    const ratio = (img.naturalWidth && img.naturalHeight) ? img.naturalHeight / img.naturalWidth : 1;
    const bw = box.x1 - box.x0, bh = (box.y1 - box.y0);
    // size of the whole image (in px) so the product fills FILL of the box
    let iw = Math.min((W * FILL) / bw, (H * FILL) / (bh * ratio));
    const ih = iw * ratio;
    const cx = (box.x0 + box.x1) / 2, cy = (box.y0 + box.y1) / 2;
    img.classList.add('loom-cfg-fit');
    img.style.cssText = `position:absolute !important;max-width:none !important;max-height:none !important;` +
      `width:${iw}px !important;height:${ih}px !important;right:auto !important;bottom:auto !important;` +
      `left:${W / 2 - cx * iw}px !important;top:${H / 2 - cy * ih}px !important;object-fit:fill !important;transform:none !important;`;
  };

  const run = (root = document) => {
    root.querySelectorAll(SEL).forEach((img) => {
      if (img.dataset.loomCfg === (img.currentSrc || img.src)) return;
      const go = () => { img.dataset.loomCfg = img.currentSrc || img.src; place(img); };
      if (img.complete && img.naturalWidth) go(); else img.addEventListener('load', go, { once: true });
    });
  };

  const init = () => {
    run();
    new MutationObserver(() => run()).observe(document.body, { childList: true, subtree: true });
    let t; window.addEventListener('resize', () => {
      clearTimeout(t); t = setTimeout(() => document.querySelectorAll(SEL).forEach((img) => place(img)), 150);
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
})();
